import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import API_BASE from "../../config/api";

const TRACK_ENDPOINT = `${API_BASE}/analytics/user-journey/track`;
const FLUSH_INTERVAL_MS = 5000;
const PAGE_PROGRESS_INTERVAL_MS = 15000;
const MAX_BATCH_SIZE = 25;
const MAX_CAPTURED_TEXT_LENGTH = 4000;
const MAX_CAPTURED_STRING_LENGTH = 500;
const REDACTED_VALUE = "[redacted]";
const REGISTRATION_TOKEN_STORAGE_KEY = "smartlend_registration_token";

const landingPrefixes = [
  "/home",
  "/new-homepage",
  "/revamp-homepage",
  "/testing-homepage",
  "/money-lender-campaign",
  "/corporate-campaign",
  "/property-campaign",
  "/landing-choco-up",
  "/ml-analysis",
  "/cbs-partnership",
  "/partner-with-us",
  "/whysmartlend",
  "/frequentlyaskedquestions",
  "/contactus",
  "/news-events",
  "/director-authorization",
  "/roadmap",
  "/privacy-policy",
  "/terms-and-conditions",
];

const dashboardExactPaths = [
  "/dashboard",
  "/b-dashboard",
  "/l-messages",
  "/b-messages",
  "/loan-request-grid",
  "/loan-request",
  "/loan",
  "/pending-acceptance",
  "/accepted-loans",
  "/l-payments",
  "/analytics",
  "/agreements",
  "/l-profile",
  "/l-document-builder",
  "/l-manage-cases",
  "/l-personal-loan-marketplace",
  "/l-manual-giro",
  "/subscription",
  "/b-personal-loans",
  "/b-loan-hub",
  "/b-instant-prop-term-loan",
  "/b-coming-soon",
  "/b-cbs-campaign",
  "/b-document-vault",
  "/b-profile",
  "/b-accepted-loans",
  "/b-requested-loans",
  "/b-loan-in-progress",
  "/b-transactions",
  "/b-agreements",
];

function getOrCreateId(storage, key, prefix) {
  try {
    const existing = storage.getItem(key);
    if (existing) return existing;

    const id = `${prefix}_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2, 12)}`;
    storage.setItem(key, id);
    return id;
  } catch (error) {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
  }
}

function classifyPath(pathname) {
  if (landingPrefixes.some((prefix) => pathname.startsWith(prefix))) {
    return "landing";
  }

  if (pathname.startsWith("/auth/") || pathname === "/something-went-wrong") {
    return "auth";
  }

  if (
    dashboardExactPaths.includes(pathname) ||
    pathname.startsWith("/b-") ||
    pathname.startsWith("/l-")
  ) {
    return "dashboard";
  }

  if (
    pathname.startsWith("/b-") ||
    pathname === "/stg-b-profile" ||
    pathname === "/dashboard-mobile" ||
    pathname === "/sidebar-mobile"
  ) {
    return "borrower";
  }

  return null;
}

function safePathFromUrl(url) {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.pathname;
  } catch (error) {
    return String(url || "").slice(0, 255);
  }
}

function normalizedApiPath(url) {
  try {
    const parsed = new URL(url, window.location.origin);
    if (!parsed.href.includes("/api/")) return null;
    return `${parsed.pathname}${parsed.search ? parsed.search.slice(0, 120) : ""}`;
  } catch (error) {
    return null;
  }
}

function isAuthFunnelApi(apiPath) {
  return (
    apiPath?.startsWith("/api/auth/") ||
    apiPath?.startsWith("/api/register/") ||
    apiPath?.startsWith("/api/registration/")
  );
}

function safeStorageGet(storage, key) {
  try {
    return storage?.getItem(key) || "";
  } catch (error) {
    return "";
  }
}

function safeCookieGet(key) {
  try {
    return Cookies.get(key) || "";
  } catch (error) {
    return "";
  }
}

function safeUrlSearchHas(key) {
  try {
    return new URLSearchParams(window.location.search).has(key);
  } catch (error) {
    return false;
  }
}

function navigationType() {
  try {
    const navigationEntry = performance
      ?.getEntriesByType?.("navigation")
      ?.slice(-1)?.[0];
    return navigationEntry?.type || null;
  } catch (error) {
    return null;
  }
}

function authStorageSnapshot() {
  const phoneNo = safeCookieGet("phoneNo");

  return {
    token_cookie_present: Boolean(safeCookieGet("token")),
    registration_token_session_present: Boolean(
      safeStorageGet(window.sessionStorage, REGISTRATION_TOKEN_STORAGE_KEY)
    ),
    registration_token_local_present: Boolean(
      safeStorageGet(window.localStorage, REGISTRATION_TOKEN_STORAGE_KEY)
    ),
    otp_session_cookie_present: Boolean(safeCookieGet("session")),
    phone_cookie_present: Boolean(phoneNo),
    phone_cookie_masked: phoneNo ? maskPhone(phoneNo) : null,
    email_cookie_present: Boolean(safeCookieGet("email")),
    user_account_type_cookie: safeCookieGet("user_account_type") || null,
    singpass_sync_cookie_present: Boolean(safeCookieGet("singpass_sync")),
    query_token_present: safeUrlSearchHas("token"),
    query_access_token_present: safeUrlSearchHas("access_token"),
  };
}

function authPageSnapshot() {
  return {
    pathname: window.location.pathname,
    visibility_state: document.visibilityState || null,
    has_focus: typeof document.hasFocus === "function" ? document.hasFocus() : null,
    online: typeof navigator.onLine === "boolean" ? navigator.onLine : null,
    referrer_path: document.referrer ? safePathFromUrl(document.referrer) : null,
    navigation_type: navigationType(),
  };
}

function describeAuthorizationHeader(value) {
  const rawValue = String(value || "").trim();

  if (!rawValue) {
    return {
      authorization_header_present: false,
      authorization_header_scheme: null,
      authorization_header_state: "missing",
    };
  }

  const [scheme = ""] = rawValue.split(/\s+/, 1);
  const lowerValue = rawValue.toLowerCase();

  let state = "present";
  if (lowerValue === "bearer" || lowerValue === "bearer ") {
    state = "bearer_empty";
  } else if (lowerValue === "bearer undefined") {
    state = "bearer_undefined";
  } else if (lowerValue === "bearer null") {
    state = "bearer_null";
  } else if (lowerValue.startsWith("bearer ")) {
    state = "bearer_present";
  } else {
    state = "non_bearer_present";
  }

  return {
    authorization_header_present: true,
    authorization_header_scheme: scheme || null,
    authorization_header_state: state,
  };
}

function readHeaderValue(headers, name) {
  if (!headers) return "";
  const targetName = name.toLowerCase();

  try {
    if (typeof Headers !== "undefined" && headers instanceof Headers) {
      return headers.get(name) || "";
    }

    if (Array.isArray(headers)) {
      const entry = headers.find(
        ([headerName]) => String(headerName).toLowerCase() === targetName
      );
      return entry?.[1] || "";
    }

    if (typeof headers === "object") {
      const entry = Object.entries(headers).find(
        ([headerName]) => headerName.toLowerCase() === targetName
      );
      return entry?.[1] || "";
    }
  } catch (error) {
    return "";
  }

  return "";
}

function captureFetchAuthorizationContext(args) {
  const requestHeaders =
    typeof Request !== "undefined" && args[0] instanceof Request
      ? args[0].headers
      : null;
  const initHeaders = args[1]?.headers;
  const authorizationHeader =
    readHeaderValue(initHeaders, "Authorization") ||
    readHeaderValue(requestHeaders, "Authorization");

  return describeAuthorizationHeader(authorizationHeader);
}

function buildAuthDebugContext({ apiPath, status, authorizationContext }) {
  if (status !== 401 && !isAuthFunnelApi(apiPath)) return null;

  return {
    api_path_is_auth_funnel: Boolean(isAuthFunnelApi(apiPath)),
    ...authStorageSnapshot(),
    ...authPageSnapshot(),
    ...authorizationContext,
  };
}

function truncateText(value, maxLength = MAX_CAPTURED_STRING_LENGTH) {
  const text = String(value ?? "");
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
}

function maskPhone(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length < 4) return REDACTED_VALUE;
  return `***${digits.slice(-4)}`;
}

function isSensitiveKey(key) {
  return /password|otp|one[_-]?time|verification[_-]?code|session|token|authorization|cookie|csrf|secret|api[_-]?key|card|cvv|cvc|nric|fin|singpass|passport/i.test(
    key || ""
  );
}

function sanitizeAnalyticsValue(value, key = "", depth = 0) {
  if (isSensitiveKey(key)) return REDACTED_VALUE;
  if (value === null || value === undefined) return value;
  if (depth > 4) return "[truncated]";

  if (typeof value === "string") {
    return truncateText(value);
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return value;
  }

  if (typeof Blob !== "undefined" && value instanceof Blob) {
    return `[${value.type || "file"} ${value.size || 0} bytes]`;
  }

  if (Array.isArray(value)) {
    return value.slice(0, 20).map((item) =>
      sanitizeAnalyticsValue(item, key, depth + 1)
    );
  }

  if (typeof value === "object") {
    return Object.entries(value)
      .slice(0, 40)
      .reduce((result, [entryKey, entryValue]) => {
        result[entryKey] = sanitizeAnalyticsValue(
          entryValue,
          entryKey,
          depth + 1
        );
        return result;
      }, {});
  }

  return truncateText(value);
}

function parseMaybeJson(text) {
  if (!text || typeof text !== "string") return text;
  const trimmed = text.trim();
  if (!trimmed) return null;

  if (
    (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
    (trimmed.startsWith("[") && trimmed.endsWith("]"))
  ) {
    try {
      return JSON.parse(trimmed);
    } catch (error) {
      return truncateText(trimmed, MAX_CAPTURED_TEXT_LENGTH);
    }
  }

  return truncateText(trimmed, MAX_CAPTURED_TEXT_LENGTH);
}

function captureRequestPayload(body) {
  if (!body) return null;

  try {
    if (typeof body === "string") {
      return sanitizeAnalyticsValue(parseMaybeJson(body));
    }

    if (typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams) {
      return sanitizeAnalyticsValue(Object.fromEntries(body.entries()));
    }

    if (typeof FormData !== "undefined" && body instanceof FormData) {
      const formPayload = {};
      body.forEach((value, key) => {
        formPayload[key] = sanitizeAnalyticsValue(value, key);
      });
      return formPayload;
    }

    if (typeof Blob !== "undefined" && body instanceof Blob) {
      return `[${body.type || "request body"} ${body.size || 0} bytes]`;
    }

    return sanitizeAnalyticsValue(body);
  } catch (error) {
    return "[payload unavailable]";
  }
}

async function captureResponsePayload(response) {
  try {
    const text = await response.clone().text();
    return sanitizeAnalyticsValue(parseMaybeJson(text));
  } catch (error) {
    return "[response unavailable]";
  }
}

function captureXhrResponsePayload(xhr) {
  try {
    if (!xhr.responseType || xhr.responseType === "text") {
      return sanitizeAnalyticsValue(parseMaybeJson(xhr.responseText));
    }

    if (xhr.responseType === "json") {
      return sanitizeAnalyticsValue(xhr.response);
    }

    return `[${xhr.responseType} response]`;
  } catch (error) {
    return "[response unavailable]";
  }
}

function getElementLabel(element) {
  const raw =
    element.getAttribute("data-analytics-label") ||
    element.getAttribute("aria-label") ||
    element.getAttribute("title") ||
    element.innerText ||
    element.value ||
    element.name ||
    element.id ||
    element.className ||
    element.tagName;

  return String(raw || "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 255);
}

function getSectionLabel(element, index) {
  const heading = element.querySelector("h1, h2, h3");
  const label =
    element.getAttribute("data-analytics-section") ||
    element.getAttribute("aria-label") ||
    element.id ||
    heading?.innerText ||
    `Section ${index + 1}`;

  return String(label || "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 255);
}

function isTrackableSection(element) {
  const ariaLabel = element.getAttribute("aria-label") || "";
  const rect = element.getBoundingClientRect();

  return (
    !element.closest(".Toastify") &&
    !/^Notifications/i.test(ariaLabel) &&
    rect.height >= 80 &&
    rect.width >= 80
  );
}

function currentScrollDepth() {
  const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
  const totalHeight =
    document.documentElement.scrollHeight - window.innerHeight || 1;
  return Math.max(0, Math.min(100, Math.round((scrollTop / totalHeight) * 100)));
}

export function UserJourneyAnalyticsTracker() {
  const location = useLocation();
  const visitorIdRef = useRef(null);
  const sessionIdRef = useRef(null);
  const queueRef = useRef([]);
  const areaRef = useRef(null);
  const pathRef = useRef(location.pathname);
  const pageStartedAtRef = useRef(Date.now());
  const maxScrollDepthRef = useRef(0);
  const maxSectionRef = useRef(null);
  const hasSentExitForPageRef = useRef(false);
  const flushTimerRef = useRef(null);
  const progressTimerRef = useRef(null);

  if (!visitorIdRef.current) {
    visitorIdRef.current = getOrCreateId(
      window.localStorage,
      "smartlend_analytics_visitor_id",
      "visitor"
    );
  }

  if (!sessionIdRef.current) {
    sessionIdRef.current = getOrCreateId(
      window.sessionStorage,
      "smartlend_analytics_session_id",
      "session"
    );
  }

  const flush = useCallback((useBeacon = false) => {
    if (!queueRef.current.length) return;

    const events = queueRef.current.splice(0, MAX_BATCH_SIZE);
    const payload = JSON.stringify({ events });

    if (useBeacon && navigator.sendBeacon) {
      const blob = new Blob([payload], { type: "application/json" });
      navigator.sendBeacon(TRACK_ENDPOINT, blob);
      return;
    }

    const headers = {
      "Content-Type": "application/json",
    };

    const token = Cookies.get("token");
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    window
      .fetch(TRACK_ENDPOINT, {
        method: "POST",
        headers,
        body: payload,
        keepalive: true,
      })
      .catch(() => {
        queueRef.current.unshift(...events.slice(0, MAX_BATCH_SIZE));
      });
  }, []);

  const enqueue = useCallback(
    (event, flushSoon = false) => {
      const area = event.area || classifyPath(window.location.pathname);
      if (!area) return;

      queueRef.current.push({
        session_id: sessionIdRef.current,
        visitor_id: visitorIdRef.current,
        area,
        page_path: window.location.pathname,
        page_title: document.title,
        occurred_at: new Date().toISOString(),
        ...event,
      });

      if (queueRef.current.length >= MAX_BATCH_SIZE || flushSoon) {
        flush(false);
      }
    },
    [flush]
  );

  const enqueuePageExit = useCallback((useBeacon = false) => {
    const area = areaRef.current;
    if (!area || hasSentExitForPageRef.current) return;

    hasSentExitForPageRef.current = true;
    const durationMs = Date.now() - pageStartedAtRef.current;
    queueRef.current.push({
      session_id: sessionIdRef.current,
      visitor_id: visitorIdRef.current,
      event_type: "page_exit",
      area,
      page_path: pathRef.current,
      page_title: document.title,
      duration_ms: durationMs,
      scroll_depth: maxScrollDepthRef.current,
      section_id: maxSectionRef.current?.id || null,
      section_label: maxSectionRef.current?.label || null,
      occurred_at: new Date().toISOString(),
    });
    flush(useBeacon);
  }, [flush]);

  const enqueuePageProgress = useCallback(() => {
    const area = areaRef.current;
    if (!area || hasSentExitForPageRef.current) return;

    queueRef.current.push({
      session_id: sessionIdRef.current,
      visitor_id: visitorIdRef.current,
      event_type: "page_progress",
      area,
      page_path: pathRef.current,
      page_title: document.title,
      duration_ms: Date.now() - pageStartedAtRef.current,
      scroll_depth: maxScrollDepthRef.current,
      section_id: maxSectionRef.current?.id || null,
      section_label: maxSectionRef.current?.label || null,
      occurred_at: new Date().toISOString(),
    });
  }, []);

  useEffect(() => {
    flushTimerRef.current = window.setInterval(() => flush(false), FLUSH_INTERVAL_MS);
    progressTimerRef.current = window.setInterval(() => {
      enqueuePageProgress();
      flush(false);
    }, PAGE_PROGRESS_INTERVAL_MS);

    const beforeUnloadHandler = () => {
      enqueuePageExit(true);
      flush(true);
    };

    window.addEventListener("pagehide", beforeUnloadHandler);
    window.addEventListener("beforeunload", beforeUnloadHandler);

    return () => {
      window.clearInterval(flushTimerRef.current);
      window.clearInterval(progressTimerRef.current);
      window.removeEventListener("pagehide", beforeUnloadHandler);
      window.removeEventListener("beforeunload", beforeUnloadHandler);
      enqueuePageExit(true);
    };
  }, [enqueuePageExit, enqueuePageProgress, flush]);

  useEffect(() => {
    if (!window.__smartlendAnalyticsTrackerInstalled) {
      window.__smartlendAnalyticsTrackerInstalled = true;

      const originalFetch = window.fetch;
      window.fetch = async (...args) => {
        const startedAt = Date.now();
        const url = typeof args[0] === "string" ? args[0] : args[0]?.url;
        const method =
          args[1]?.method ||
          (typeof args[0] === "object" && args[0]?.method) ||
          "GET";
        const apiPath = normalizedApiPath(url);
        const shouldTrackApi =
          apiPath && !apiPath.includes("/analytics/user-journey/track");
        const requestPayload = captureRequestPayload(args[1]?.body);
        const authorizationContext = captureFetchAuthorizationContext(args);

        try {
          const response = await originalFetch(...args);
          if (shouldTrackApi && response.status >= 400) {
            const responsePayload = await captureResponsePayload(response);
            const authDebugContext = buildAuthDebugContext({
              apiPath,
              status: response.status,
              authorizationContext,
            });
            window.dispatchEvent(
              new CustomEvent("smartlend:api-error", {
                detail: {
                  method,
                  apiPath,
                  status: response.status,
                  durationMs: Date.now() - startedAt,
                  metadata: {
                    request_payload: requestPayload,
                    response_payload: responsePayload,
                    response_status_text: response.statusText || null,
                    auth_debug_context: authDebugContext,
                  },
                },
              })
            );
          }
          return response;
        } catch (error) {
          if (shouldTrackApi) {
            const authDebugContext = buildAuthDebugContext({
              apiPath,
              status: 0,
              authorizationContext,
            });
            window.dispatchEvent(
              new CustomEvent("smartlend:api-error", {
                detail: {
                  method,
                  apiPath,
                  status: 0,
                  durationMs: Date.now() - startedAt,
                  metadata: {
                    request_payload: requestPayload,
                    network_error: truncateText(error?.message || error),
                    auth_debug_context: authDebugContext,
                  },
                },
              })
            );
          }
          throw error;
        }
      };

      const originalOpen = XMLHttpRequest.prototype.open;
      const originalSetRequestHeader = XMLHttpRequest.prototype.setRequestHeader;
      const originalSend = XMLHttpRequest.prototype.send;

      XMLHttpRequest.prototype.open = function patchedOpen(method, url, ...rest) {
        this.__smartlendAnalytics = {
          method,
          apiPath: normalizedApiPath(url),
          startedAt: Date.now(),
          authorizationContext: describeAuthorizationHeader(""),
        };
        return originalOpen.call(this, method, url, ...rest);
      };

      XMLHttpRequest.prototype.setRequestHeader = function patchedSetRequestHeader(
        name,
        value
      ) {
        if (
          this.__smartlendAnalytics &&
          String(name || "").toLowerCase() === "authorization"
        ) {
          this.__smartlendAnalytics.authorizationContext =
            describeAuthorizationHeader(value);
        }

        return originalSetRequestHeader.call(this, name, value);
      };

      XMLHttpRequest.prototype.send = function patchedSend(...args) {
        if (this.__smartlendAnalytics) {
          this.__smartlendAnalytics.requestPayload = captureRequestPayload(args[0]);
        }

        this.addEventListener("loadend", function handleLoadEnd() {
          const details = this.__smartlendAnalytics;
          if (
            details?.apiPath &&
            !details.apiPath.includes("/analytics/user-journey/track") &&
            this.status >= 400
          ) {
            const authDebugContext = buildAuthDebugContext({
              apiPath: details.apiPath,
              status: this.status,
              authorizationContext: details.authorizationContext,
            });
            window.dispatchEvent(
              new CustomEvent("smartlend:api-error", {
                detail: {
                  method: details.method,
                  apiPath: details.apiPath,
                  status: this.status,
                  durationMs: Date.now() - details.startedAt,
                  metadata: {
                    request_payload: details.requestPayload || null,
                    response_payload: captureXhrResponsePayload(this),
                    response_status_text: this.statusText || null,
                    auth_debug_context: authDebugContext,
                  },
                },
              })
            );
          }
        });

        return originalSend.call(this, ...args);
      };
    }
  }, [enqueue]);

  useEffect(() => {
    const handleApiError = (event) => {
      enqueue({
        event_type: "api_error",
        api_method: event.detail.method,
        api_path: event.detail.apiPath,
        api_status: event.detail.status,
        api_duration_ms: event.detail.durationMs,
        is_error: true,
        metadata: event.detail.metadata || null,
      });
    };

    window.addEventListener("smartlend:api-error", handleApiError);
    return () => window.removeEventListener("smartlend:api-error", handleApiError);
  }, [enqueue]);

  useEffect(() => {
    const handleCustomAnalyticsEvent = (event) => {
      if (!event.detail?.event_type) return;

      enqueue(
        {
          area: classifyPath(window.location.pathname) || "dashboard",
          ...event.detail,
        },
        event.detail.flushSoon !== false
      );
    };

    window.addEventListener(
      "smartlend:analytics-event",
      handleCustomAnalyticsEvent
    );
    return () =>
      window.removeEventListener(
        "smartlend:analytics-event",
        handleCustomAnalyticsEvent
      );
  }, [enqueue]);

  useEffect(() => {
    const area = classifyPath(location.pathname);
    const previousArea = areaRef.current;

    if (previousArea && pathRef.current !== location.pathname) {
      enqueuePageExit(false);
    }

    areaRef.current = area;
    pathRef.current = location.pathname;
    pageStartedAtRef.current = Date.now();
    maxScrollDepthRef.current = 0;
    maxSectionRef.current = null;
    hasSentExitForPageRef.current = false;

    if (area) {
      enqueue(
        {
          event_type: "page_view",
          area,
          page_path: location.pathname,
          metadata: {
            referrer: document.referrer || null,
            viewport_width: window.innerWidth,
            viewport_height: window.innerHeight,
          },
        },
        true
      );
    }
  }, [enqueue, enqueuePageExit, location.pathname]);

  useEffect(() => {
    const handleClick = (event) => {
      const area = classifyPath(window.location.pathname);
      if (!area) return;

      if (!event.target?.closest) return;

      const element = event.target.closest(
        "button, a, [role='button'], input[type='button'], input[type='submit']"
      );
      if (!element) return;

      enqueue({
        event_type: "click",
        area,
        target_label: getElementLabel(element),
        target_path: element.href ? safePathFromUrl(element.href) : null,
      });
    };

    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, [enqueue]);

  useEffect(() => {
    const area = classifyPath(location.pathname);
    if (!area) return undefined;

    const handleScroll = () => {
      maxScrollDepthRef.current = Math.max(
        maxScrollDepthRef.current,
        currentScrollDepth()
      );
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    let observer = null;
    if ((area === "landing" || area === "dashboard") && "IntersectionObserver" in window) {
      const seenSections = new Set();
      const sections = Array.from(
        document.querySelectorAll("section, [data-analytics-section]")
      ).filter(isTrackableSection);

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            const index = sections.indexOf(entry.target);
            const sectionId =
              entry.target.getAttribute("data-analytics-section-id") ||
              entry.target.id ||
              `section-${index + 1}`;
            const sectionLabel = getSectionLabel(entry.target, index);

            maxSectionRef.current = { id: sectionId, label: sectionLabel };

            if (!seenSections.has(sectionId)) {
              seenSections.add(sectionId);
              enqueue({
                event_type: "section_view",
                area,
                section_id: sectionId,
                section_label: sectionLabel,
                scroll_depth: currentScrollDepth(),
              });
            }
          });
        },
        { threshold: 0.35 }
      );

      sections.forEach((section) => observer.observe(section));
    }

    return () => {
      window.removeEventListener("scroll", handleScroll);
      observer?.disconnect();
    };
  }, [enqueue, location.pathname]);

  return null;
}

export default UserJourneyAnalyticsTracker;
